import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsExporting from 'highcharts/modules/exporting';
import highchartsExportData from 'highcharts/modules/export-data';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import { useAppContext } from '../../contexts/AppContext';
import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChartHelpTooltip from '../common/ChartHelpTooltip';

highchartsExporting(Highcharts);
highchartsExportData(Highcharts);
noDataToDisplay(Highcharts);

type Props = {
  data: any;
  name: string;
};

const zones = [
  {
    name: 'High',
    color: 'rgba(255, 0, 0, 0.5)',
    fillColor: 'rgba(255, 0, 0, 1)',
    data: [
      [0, 0],
      [0.32, 0],
      [0.4, 0.55],
      [1, 0.7],
    ],
  },
  {
    name: 'Low',
    color: 'rgba(0, 255, 0, 0.5)',
    fillColor: 'rgba(0, 255, 0, 1)',
    data: [
      [0, 1],
      [0.32, 1],
      [0.4, 0.45],
      [1, 0.3],
    ],
  },
];

const SoilingRisk = ({ data, name }: Props) => {
  const { isDarkMode } = useAppContext();
  const [chartOptions, setChartOptions] = useState<any>(null);
  const chartComponent = useRef(null);

  useEffect(() => {
    if (data?.hasOwnProperty('scores') && data?.scores?.hasOwnProperty('deposition') && data?.scores?.hasOwnProperty('rain')) {
      prepareHighcharts(prepareData());
    }
  }, [data]);

  const prepareData = () => {
    let hc_data = {
      name: name ? name : 'PV Plant',
      data: [[data?.scores?.rain, data?.scores?.deposition]],
      color: 'black',
      type: 'scatter',
    };
    let timeseries_data = [hc_data, ...zones];
    return timeseries_data;
  };
  const prepareHighcharts = (highChartsData: any) => {
    const chart = {
      chart: {
        type: 'area',
        zoomType: 'x',
        events: {
          load: function (this: any): any {
            (this.pointer.reset = () => {
              this.series.forEach((serie: any, i: any) => serie.setState(i === 0 ? 'hover' : 'inactive'));
              this.tooltip.refresh(this.series[0].data[0]);
              return undefined;
            }),
              this.series.forEach((serie: any, i: any) => serie.setState(i === 0 ? 'hover' : 'inactive'));
            this.tooltip.refresh(this.series[0].data[0]);
          },
        },
      },
      title: {
        text: 'Soiling Risk',
        // align: 'left',
      },
      xAxis: {
        title: { text: 'Rain Score' },
        max: 1,
        gridLineWidth: 1,
      },
      yAxis: [
        {
          title: { text: 'Deposition Score' },
          max: 1,
          gridLineWidth: 1,
        },
      ],
      legend: {
        enabled: true,
      },
      tooltip: {
        valueDecimals: 2,
        formatter: function (this: any): any {
          let result: string =
            `<span style='font-size: "smaller"'><b>` +
            this.series.name +
            `</b></span><br/>` +
            '<span style="color:' +
            this.point.color +
            '">\u25CF</span>Rain Score: <b>' +
            this.point.x.toFixed(2) +
            '</b><br/>' +
            '<span style="color:' +
            this.point.color +
            '">\u25CF</span>Deposition Score: <b>' +
            this.point.y.toFixed(2) +
            '</b><br/>';
          return result;
        },
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        area: {
          stacking: 'normal',
          marker: {
            enabled: false,
          },
        },
      },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      series: highChartsData,
    };
    setChartOptions(chart);
  };

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        <>
          <Box display="flex" alignItems="center">
            <Typography variant="h5" color="text.primary" sx={{ mt: 0 }}>
              Soiling Risk
            </Typography>
            <ChartHelpTooltip chartType="soilingRisk" />
          </Box>
          {chartOptions && (
            <HighchartsReact
              ref={chartComponent}
              containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
              highcharts={Highcharts}
              options={chartOptions}
            />
          )}
        </>
      </Box>
    </>
  );
};

export default SoilingRisk;
